import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchAnalyticsConfig, fetchAnalytics } from '../api';
import type {
    AnalyticsConfig,
    AnalyticsData,
    AnalyticsParameters,
    RelativeTimePeriod,
    VisualizationResult,
} from '../types';
import { useWeavableAuth } from './useWeavableAuth';

/**
 * Hook to fetch analytics configurations for a connection
 */
export function useAnalyticsConfig(preset?: string) {
    const { baseUrl, connectionId, getToken, onError } = useWeavableAuth();
    const [configs, setConfigs] = useState<AnalyticsConfig[]>([]);
    const [timePeriod, setTimePeriod] = useState<RelativeTimePeriod>('this_quarter');
    const [includeDelta, setIncludeDelta] = useState<boolean>(true);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<Error | null>(null);

    const fetch = useCallback(async () => {
        setLoading(true);
        setError(null);

        try {
            const token = await getToken();
            const data = await fetchAnalyticsConfig(baseUrl, connectionId, token, preset);
            setConfigs(data.analytics);
            setTimePeriod(data.timePeriod);
            setIncludeDelta(data.includeDelta);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch analytics config');
            setError(error);
            onError?.(error);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, connectionId, getToken, onError, preset]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return { configs, timePeriod, includeDelta, loading, error, refetch: fetch };
}

/**
 * Hook to fetch and execute a single analytics.
 *
 * `parameters` is a flat map of overrides for the targeted insight (e.g. `{ lookAheadDays: 30 }`).
 */
export function useAnalytics(
    analyticsId: string,
    timePeriod: RelativeTimePeriod,
    userIanaZone?: string,
    includeDelta?: boolean,
    enabled = true,
    parameters?: Record<string, number | boolean | string>,
) {
    const { baseUrl, connectionId, getToken, onError } = useWeavableAuth();
    const [visualization, setVisualization] = useState<VisualizationResult | null>(null);
    const [analysisId, setAnalysisId] = useState<string | null>(null);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState<Error | null>(null);

    // Stabilize `parameters` by value so callers don't have to memoize — a fresh object literal
    // with identical contents on each render should not retrigger the fetch.
    const parametersKey = parameters ? JSON.stringify(parameters) : '';
    const stableParameters = useMemo(
        () => parameters,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [parametersKey],
    );

    const fetch = useCallback(async () => {
        if (!enabled || !timePeriod) {
            setVisualization(null);
            setAnalysisId(null);
            return;
        }

        setLoading(true);
        setError(null);

        try {
            const token = await getToken();
            const data = await fetchAnalytics(
                baseUrl,
                connectionId,
                analyticsId,
                timePeriod,
                userIanaZone,
                includeDelta,
                token,
                stableParameters,
            );
            setVisualization(data.visualization);
            setAnalysisId(data.analysisId);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch analytics');
            setError(error);
            onError?.(error);
        } finally {
            setLoading(false);
        }
    }, [
        baseUrl,
        connectionId,
        analyticsId,
        timePeriod,
        userIanaZone,
        includeDelta,
        getToken,
        onError,
        enabled,
        stableParameters,
    ]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return { visualization, analysisId, loading, error, refetch: fetch };
}

/**
 * Hook to fetch multiple analytics for all enabled configs.
 *
 * `parameters` is a per-insight map keyed by analyticsId. For each insight, only the entry
 * under its own id is forwarded to `fetchAnalytics`, so values can never leak across insights.
 */
export function useAllAnalytics(
    configs: AnalyticsConfig[],
    timePeriod: RelativeTimePeriod,
    userIanaZone?: string,
    includeDelta?: boolean,
    enabled = true,
    parameters?: AnalyticsParameters,
) {
    const { baseUrl, connectionId, getToken, onError } = useWeavableAuth();
    const [analytics, setAnalytics] = useState<AnalyticsData[]>([]);
    const [loading, setLoading] = useState(enabled);
    const [error, setError] = useState<Error | null>(null);

    // Stabilize `parameters` by value so callers don't have to memoize — a fresh object literal
    // with identical contents on each render should not retrigger the fetch.
    const parametersKey = parameters ? JSON.stringify(parameters) : '';
    const stableParameters = useMemo(
        () => parameters,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [parametersKey],
    );

    const fetch = useCallback(async () => {
        if (!enabled || !timePeriod || configs.length === 0) {
            setAnalytics([]);
            return;
        }

        setLoading(true);
        setError(null);

        const enabledConfigs = configs.filter((c) => c.enabled);

        try {
            const token = await getToken();
            const results = await Promise.all(
                enabledConfigs.map(async (config) => {
                    try {
                        const data = await fetchAnalytics(
                            baseUrl,
                            connectionId,
                            config.id,
                            timePeriod,
                            userIanaZone,
                            includeDelta,
                            token,
                            stableParameters?.[config.id],
                        );
                        return {
                            config,
                            visualization: data.visualization,
                            analysisId: data.analysisId,
                            loading: false,
                            error: null,
                        };
                    } catch (err) {
                        return {
                            config,
                            visualization: null,
                            analysisId: null,
                            loading: false,
                            error: err instanceof Error ? err : new Error('Failed'),
                        };
                    }
                }),
            );

            setAnalytics(results);
        } catch (err) {
            const error = err instanceof Error ? err : new Error('Failed to fetch analytics');
            setError(error);
            onError?.(error);
        } finally {
            setLoading(false);
        }
    }, [
        baseUrl,
        connectionId,
        configs,
        timePeriod,
        userIanaZone,
        includeDelta,
        getToken,
        onError,
        enabled,
        stableParameters,
    ]);

    useEffect(() => {
        fetch();
    }, [fetch]);

    return { analytics, loading, error, refetch: fetch };
}
